import { RefreshCcw, UserPlus } from 'lucide-react';
import { useEffect, useState } from 'react';
import SortableTable from '../../components/SortableTable';
import {
  fetchBotBusinesses,
  fetchBotRecentBills,
  fetchBotStatus,
  fetchPlatformIntegrations,
  registerBotBusiness,
} from '../../services/integrations';

function formatTime(value) {
  const date = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export default function WhatsAppBot() {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [bills, setBills] = useState([]);
  const [filter, setFilter] = useState('');
  const [registerOpen, setRegisterOpen] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', phone: '', businessId: '' });

  const load = async () => {
    setLoading(true);
    setError('');
    const config = await fetchPlatformIntegrations();
    const serverUrl = config.bot?.serverUrl;
    const [botStatus, botBusinesses, recentBills] = await Promise.all([
      fetchBotStatus(serverUrl),
      fetchBotBusinesses(serverUrl),
      fetchBotRecentBills(serverUrl),
    ]);
    setStatus(botStatus);
    setBusinesses(botBusinesses);
    setBills(recentBills);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const submitRegistration = async () => {
    const payload = {
      name: form.name.trim(),
      phone: form.phone.replace(/\D/g, ''),
      businessId: form.businessId.trim() || null,
    };
    if (!payload.name || payload.phone.length < 10) {
      setError('Business name and a valid phone number are required.');
      return;
    }
    setRegistering(true);
    setError('');
    try {
      await registerBotBusiness(status.serverUrl, payload);
      setRegisterOpen(false);
      setForm({ name: '', phone: '', businessId: '' });
      await load();
    } catch (err) {
      setError(err?.message || 'Failed to register business with bot server.');
    } finally {
      setRegistering(false);
    }
  };

  if (loading || !status) {
    return (
      <div className="wb-stack">
        <div className="wb-skeleton wb-skeleton--large" />
        <div className="wb-skeleton wb-skeleton--table" />
      </div>
    );
  }

  return (
    <div className="wb-stack">
      {error ? <p className="wb-error-text">{error}</p> : null}
      <section className="wb-card wb-card--elevated">
        <div className="wb-card-head">
          <div>
            <h3 className="wb-section-title">Bot Server Status</h3>
            <p className="wb-muted">Server URL: {status.serverUrl}</p>
          </div>
          <span className={`wb-status-pill ${status.online ? 'is-online' : 'is-offline'}`}>
            {status.online ? 'Online' : 'Offline'}
          </span>
        </div>
        <div className="wb-kpi-grid">
          <div className="wb-kpi"><span>Uptime</span><strong>{status.uptime}</strong></div>
          <div className="wb-kpi"><span>Latency</span><strong>{status.latencyMs}ms</strong></div>
          <div className="wb-kpi"><span>Last health check</span><strong>{formatTime(status.lastHealthCheck)}</strong></div>
        </div>
      </section>

      <section className="wb-card">
        <div className="wb-card-head">
          <h3 className="wb-section-title">Registered Businesses</h3>
          <div className="wb-actions-line">
            <input className="wb-filter-input" placeholder="Filter businesses" value={filter} onChange={(event) => setFilter(event.target.value)} />
            <button type="button" className="secondary" onClick={() => setRegisterOpen(true)}><UserPlus size={16} /> Register New Business</button>
            <button type="button" onClick={load}><RefreshCcw size={16} /> Refresh</button>
          </div>
        </div>
        <SortableTable
          rows={businesses}
          filter={filter}
          rowKey={(row) => row.id || row.phone}
          columns={[
            { key: 'name', header: 'Business Name', accessor: (row) => row.name || row.businessName },
            { key: 'phone', header: 'Phone', accessor: 'phone' },
            { key: 'status', header: 'Status', accessor: 'status', render: (row) => <span className="wb-status-pill is-online">{row.status || 'Active'}</span> },
          ]}
        />
      </section>

      {registerOpen ? (
        <div className="wb-drawer-backdrop">
          <div className="wb-drawer">
            <h3>Register New Business</h3>
            <label className="wb-field">
              <span>Business Name</span>
              <input value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} />
            </label>
            <label className="wb-field">
              <span>Phone</span>
              <input value={form.phone} onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))} placeholder="919876543210" />
            </label>
            <label className="wb-field">
              <span>Business ID</span>
              <input value={form.businessId} onChange={(event) => setForm((current) => ({ ...current, businessId: event.target.value }))} placeholder="Optional" />
            </label>
            <div className="wb-modal-actions">
              <button type="button" className="secondary" onClick={() => setRegisterOpen(false)}>Cancel</button>
              <button type="button" disabled={registering} onClick={submitRegistration}>
                {registering ? 'Registering...' : 'Register'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <section className="wb-card">
        <h3 className="wb-section-title">Recent Bills</h3>
        <SortableTable
          rows={bills}
          rowKey={(row) => row.id || `${row.business}-${row.time}`}
          columns={[
            { key: 'time', header: 'Time', accessor: 'time', render: (row) => formatTime(row.time) },
            { key: 'business', header: 'Business', accessor: 'business' },
            { key: 'total', header: 'Total', accessor: 'total', render: (row) => `Rs ${Number(row.total || 0).toFixed(2)}` },
            { key: 'status', header: 'Status', accessor: 'status', render: (row) => `OK ${row.status || 'created'}` },
          ]}
        />
      </section>
    </div>
  );
}
